import React, { useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { okaidia } from "react-syntax-highlighter/dist/esm/styles/prism";

const ChatItem = ({ index, timestamp, title, content, filenames, chatgpt_logo }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(content)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      })
      .catch((err) => console.error("Failed to copy:", err));
  };

  const renderContent = (text) => {
    const codeBlockRegex = /(```[\s\S]*?```)/g;
    const headerRegex = /^(#+)\s/;
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Eliminar referencias tipo  
    text = text.replace(/【\d+:\d+†source】/g, '');

    const linkify = (line, lineIndex) => {
      const elements = [];
      let lastIndex = 0;
      let match;

      // Primero enlaces Markdown
      while ((match = markdownLinkRegex.exec(line)) !== null) {
        const [fullMatch, textLabel, url] = match;
        const matchStart = match.index;

        if (matchStart > lastIndex) {
          elements.push(
            <React.Fragment key={`${lineIndex}-text-${lastIndex}`}>
              {line.slice(lastIndex, matchStart)}
            </React.Fragment>
          );
        }

        elements.push(
          <a
            key={`${lineIndex}-mdlink-${matchStart}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            {textLabel}
          </a>
        );

        lastIndex = matchStart + fullMatch.length;
      }

      const remaining = line.slice(lastIndex);
      const parts = remaining.split(urlRegex);

      parts.forEach((part, i) => {
        if (urlRegex.test(part)) {
          elements.push(
            <a
              key={`${lineIndex}-url-${i}`}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              {part}
            </a>
          );
        } else {
          elements.push(
            <React.Fragment key={`${lineIndex}-frag-${i}`}>{part}</React.Fragment>
          );
        }
      });

      return elements;
    };

    const parts = text.split(codeBlockRegex);

    return parts.map((part, index) => {
      if (codeBlockRegex.test(part)) {
        const codeContent = part.replace(/```/g, "").trim();
        return (
          <SyntaxHighlighter key={index} language="javascript" style={okaidia}>
            {codeContent}
          </SyntaxHighlighter>
        );
      } else {
        return part.split("\n").map((line, lineIndex) => {
          const headerMatch = line.match(headerRegex);
          const headerLevel = headerMatch ? headerMatch[1].length : 0;

          if (headerLevel === 1) {
            return (
              <h1 key={lineIndex} className="text-3xl font-bold text-gray-900">
                {line.replace(headerRegex, "")}
              </h1>
            );
          } else if (headerLevel === 2) {
            return (
              <h2 key={lineIndex} className="text-2xl font-semibold text-gray-900">
                {line.replace(headerRegex, "")}
              </h2>
            );
          } else if (headerLevel === 3) {
            return (
              <h3 key={lineIndex} className="text-xl font-medium text-gray-900">
                {line.replace(headerRegex, "")}
              </h3>
            );
          } else {
            return (
              <p key={lineIndex} className="text-gray-700 text-lg my-2">
                {linkify(line, lineIndex)}
              </p>
            );
          }
        });
      }
    });
  };

  return (
    <>
      <div key={index} className="relative mb-8 p-4 bg-white">
        <div className="text-right">
          <p className="text-gray-400 text-xs md:text-sm mb-1">{timestamp}</p>
          {filenames?.map((filename, index) => (
            <p className="mb-2" key={index}>
              <span className="rounded-lg p-2 whitespace-nowrap">
                <i className="fa-regular fa-file p-2 bg-blue-500 me-2 rounded-lg text-white"></i>
                {filename}
              </span>
            </p>
          ))}
          <p className="text-gray-500 mb-2 text-md md:text-lg font-semibold">
            {renderContent(title)} &nbsp;
          </p>
        </div>
        <div className="relative mb-8 p-4 bg-white">
          <p className="text-gray-400 text-xs md:text-sm mb-2 flex items-center">
            <img
              src={chatgpt_logo}
              alt="Logo"
              className="h-4 w-4 md:h-5 md:w-5 mr-2"
            />
            {timestamp}
          </p>
          <div className="text-gray-900 text-sm md:text-md leading-relaxed">
            {renderContent(content)}
            <i
              className={`fa ${copied ? "fa-check text-green-600" : "fa-copy cursor-pointer"} text-gray-500 hover:text-gray-700`}
              onClick={copyToClipboard}
            ></i>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatItem;